import React from 'react';
import { motion } from 'framer-motion';
import { MessageCircleIcon, MapPinIcon, ShieldCheckIcon, FileTextIcon, SettingsIcon, ArrowRightIcon, ArrowDownIcon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { PERMIS_IMAGES } from '../../data/permisHero';
import { solutionCopy, operatingSteps, type Bi } from '../../data/permisSolution';
import { QuestionCard } from './PermisCards';
import { PropertyContextCard, VerifiedAnswerCard } from './solution/SolutionCards';
import { ReadyPhone, CaseDashboard, OperationalCards } from './solution/SolutionDevices';
const EASE = [0.23, 1, 0.32, 1] as const;
const STEP_ICONS = {
  chat: MessageCircleIcon,
  pin: MapPinIcon,
  shield: ShieldCheckIcon,
  doc: FileTextIcon,
  gear: SettingsIcon
} as const;
export function PermisSolution() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const pick = (v: Bi) => isFR ? v.fr : v.en;
  const reveal = {
    initial: {
      opacity: 0,
      y: 12
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      margin: '-80px'
    },
    transition: {
      duration: 0.3,
      ease: EASE
    }
  };
  return <div className="max-w-[1640px] mx-auto" data-id="element-5121" data-mp="4y4erglitqau">
      <div className="grid grid-cols-1 xl:grid-cols-[32fr_68fr] gap-10 xl:gap-4 items-start" data-id="element-5122" data-mp="4wtcjl7q4qlu">
        {/* Left — editorial */}
        <motion.div {...reveal} className="relative z-10 min-w-0 xl:pt-2" data-id="element-5123" data-mp="dqzoonlxoa6y">
          <p className="font-sans text-[13px] font-semibold tracking-[0.3em] uppercase text-[#4F46E5]" data-id="element-5124" data-mp="dtc5uyjvagiy">{pick(solutionCopy.eyebrow)}</p>
          <h2 className="font-serif text-[2.5rem] sm:text-[3.2rem] 2xl:text-[3.9rem] leading-[1.03] tracking-tight mt-5" data-id="element-5125" data-mp="3i4xtr54mdrwi">
            <span className="block text-[#121A3A]" data-id="element-5126" data-mp="3m5zxnz9hes4t">{pick(solutionCopy.line1)}</span>
            <span className="block text-[#121A3A]" data-id="element-5127" data-mp="3r6e2l8fsdlym">{pick(solutionCopy.line2)}</span>
            <span className="block text-[#4F46E5]" data-id="element-5128" data-mp="11h29qjrfkfw2">{pick(solutionCopy.line3)}</span>
            <span className="block text-[#4F46E5]" data-id="element-5129" data-mp="16fws5klhk2fn">{pick(solutionCopy.line4)}</span>
          </h2>
          <p className="font-sans text-[17px] md:text-[18px] text-[#5B6478] leading-relaxed mt-7 max-w-[500px]" data-id="element-5130" data-mp="32j1kd2gf6mqq">
            <strong className="font-semibold text-[#121A3A]" data-id="element-5131" data-mp="2yhq07cgbetpx">{pick(solutionCopy.bodyLead)}</strong>
            {pick(solutionCopy.bodyRest)}
          </p>
        </motion.div>

        {/* Right — desktop composition */}
        <motion.div {...reveal} transition={{
        duration: 0.3,
        delay: 0.05,
        ease: EASE
      }} className="hidden xl:block relative h-[640px]" data-id="element-5132" data-mp="37huvwpc5c3o9">
          <img src={PERMIS_IMAGES.house} alt="" className="absolute right-0 top-0 w-[82%] h-[360px] object-cover object-[60%_40%]" style={{
          WebkitMaskImage: 'linear-gradient(to bottom, #000 55%, transparent 100%), linear-gradient(to right, transparent 0%, #000 25%)',
          WebkitMaskComposite: 'source-in',
          maskImage: 'linear-gradient(to bottom, #000 55%, transparent 100%), linear-gradient(to right, transparent 0%, #000 25%)',
          maskComposite: 'intersect'
        }} data-id="element-5133" data-mp="2jstkqubxiefa" />
          <div className="absolute left-[14%] top-[4%] w-[400px] z-20" data-id="element-5134" data-mp="bjdo2t7yu86g">
            <QuestionCard isFR={isFR} data-id="element-5135" data-mp="gbwzpkn3et8m" />
          </div>
          <div className="absolute left-0 top-[23%] w-[210px] z-20" data-id="element-5136" data-mp="gdhw7fsywm94">
            <PropertyContextCard isFR={isFR} data-id="element-5137" data-mp="2ltlngyda5u9" />
          </div>
          <div className="absolute left-[18.5%] top-[23%] w-[215px] h-[375px] z-20" data-id="element-5138" data-mp="6lc7cey0iky5">
            <VerifiedAnswerCard isFR={isFR} data-id="element-5139" data-mp="ubo9taahnphc" />
          </div>
          <div className="absolute left-[39%] top-[18%] z-30" data-id="element-5140" data-mp="uc7s4s41wfvw">
            <ReadyPhone isFR={isFR} data-id="element-5141" data-mp="zardoreqfpnq" />
          </div>
          <div className="absolute right-0 top-[21%] w-[530px] z-10" data-id="element-5142" data-mp="z85at52sdybu">
            <CaseDashboard isFR={isFR} data-id="element-5143" data-mp="la89jvde13qu" />
          </div>
          <div className="absolute right-[1%] top-[64%] w-[480px] z-20" data-id="element-5144" data-mp="q9bhbnkr5o2i">
            <OperationalCards isFR={isFR} data-id="element-5145" data-mp="qbnxpwav3qdg" />
          </div>
        </motion.div>

        {/* Right — tablet / mobile stack */}
        <div className="xl:hidden space-y-4" data-id="element-5146" data-mp="3b0le8qql30c6">
          <div className="relative rounded-3xl overflow-hidden p-4 sm:p-6" data-id="element-5147" data-mp="3b1db04vx3441">
            <img src={PERMIS_IMAGES.house} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" data-id="element-5148" data-mp="3qaqoanzbiftu" />
            <div className="relative max-w-[420px]" data-id="element-5149" data-mp="3lhff4g719myr">
              <QuestionCard isFR={isFR} data-id="element-5150" data-mp="3gje149ogsdu5" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-5151" data-mp="3ghjgkwh3unql">
            <PropertyContextCard isFR={isFR} data-id="element-5152" data-mp="3cjvg7dwn82rb" />
            <VerifiedAnswerCard isFR={isFR} data-id="element-5153" data-mp="d2x363dz1n9v" />
          </div>
          <div className="flex justify-center pt-2" data-id="element-5154" data-mp="d2dvrgly6ha3">
            <ReadyPhone isFR={isFR} data-id="element-5155" data-mp="83u9ff3fz3h7" />
          </div>
          <CaseDashboard isFR={isFR} data-id="element-5156" data-mp="81zldrwviv8w" />
          <OperationalCards isFR={isFR} data-id="element-5157" data-mp="41z84aligq3f" />
        </div>
      </div>

      {/* Operating model */}
      <ol className="mt-12 xl:mt-4 pt-8 border-t border-[#EEEEF4] grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 lg:gap-4" data-id="element-5158" data-mp="3v9bia5nz839u">
        {operatingSteps.map((s, i) => {
        const Icon = STEP_ICONS[s.key];
        return <li key={s.key} className="relative flex items-start gap-4" data-id="element-5159" data-mp="2js1yfi0xr980" data-mpi={i}>
              <span className="w-[72px] h-[72px] rounded-full bg-[#F1F0FD] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-5160" data-mp="zcdnzse2i4q4">
                <Icon className="w-8 h-8 text-[#4F46E5]" strokeWidth={1.6} data-id="element-5161" data-mp="14ax95qku7chf" />
              </span>
              <span className="min-w-0" data-id="element-5162" data-mp="18azcs5a4ufzc">
                <span className="w-6 h-6 rounded-full bg-[#4F46E5] text-white font-sans text-[12px] font-semibold flex items-center justify-center" data-id="element-5163" data-mp="1d2qwpwys9ngi">{i + 1}</span>
                <span className="block mt-2 font-sans text-[15px] font-semibold tracking-[0.14em] uppercase text-[#4F46E5]" data-id="element-5164" data-mp="1i1txh57qd7io">{pick(s.verb)}</span>
                <span className="block font-sans text-[14.5px] text-[#5B6478] leading-snug mt-1" data-id="element-5165" data-mp="1rzqc46zvmklj">{pick(s.body)}</span>
              </span>
              {i < operatingSteps.length - 1 && <>
                  <ArrowRightIcon className="hidden lg:block absolute -right-3 top-6 w-5 h-5 text-[#9A9FB5]" strokeWidth={1.6} aria-hidden="true" data-id="element-5166" data-mp="3tseiuqbqbq62" />
                  <ArrowDownIcon className="sm:hidden absolute left-[28px] -bottom-5 w-4 h-4 text-[#C9C5F5]" aria-hidden="true" data-id="element-5167" data-mp="1pwtsw0ms78l" />
                </>}
            </li>;
      })}
      </ol>

      {/* Final statement */}
      <motion.div {...reveal} className="mt-14 text-center" data-id="element-5168" data-mp="1dzxpq3l9ewky">
        <p className="font-serif text-[1.7rem] sm:text-[2.2rem] leading-snug text-[#121A3A]" data-id="element-5169" data-mp="1dx33c6ior1ow">{pick(solutionCopy.final1)}</p>
        <p className="font-serif text-[1.7rem] sm:text-[2.2rem] leading-snug text-[#4F46E5]" data-id="element-5170" data-mp="1nxc66376nld9">{pick(solutionCopy.final2)}</p>
      </motion.div>
    </div>;
}